import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { oltService, OLT, ConnectionResult } from '../../services/oltService';
import OLTForm from '../../components/forms/OLTForm';
import { useNavigate } from 'react-router-dom';

interface OltsTabProps {
  createTrigger?: number;
}

const OltsTab: React.FC<OltsTabProps> = ({ createTrigger = 0 }) => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [olts, setOlts] = useState<OLT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Teste de conexão
  const [testConnectionDialog, setTestConnectionDialog] = useState<{
    open: boolean;
    olt: OLT | null;
    testing: boolean;
    result: ConnectionResult | null;
  }>({
    open: false,
    olt: null,
    testing: false,
    result: null
  });

  // Modal para formulário OLT
  const [oltFormDialog, setOltFormDialog] = useState<{
    open: boolean;
    olt: OLT | null; // null para criação, OLT para edição
  }>({
    open: false,
    olt: null
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadOLTs();
  }, [page, rowsPerPage, searchTerm, vendorFilter, statusFilter]);

  const loadOLTs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await oltService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        vendor: vendorFilter || undefined,
        status: statusFilter || undefined
      });

      if (response && response.olts) {
        setOlts(response.olts);
        setTotalCount(response.pagination?.total || 0);
      } else {
        throw new Error('Erro ao carregar OLTs');
      }

    } catch (err: any) {
      console.error('Erro ao carregar OLTs:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (olt: OLT) => {
    setTestConnectionDialog({
      open: true,
      olt,
      testing: true,
      result: null
    });

    try {
      const response = await oltService.testConnection(olt.id);
      let result: any = response;
      if (response && typeof response === 'object') {
        if ('data' in (response as any) && (response as any).data && typeof (response as any).data === 'object') {
          result = (response as any).data.data || (response as any).data;
        }
      }
      setTestConnectionDialog(prev => ({
        ...prev,
        testing: false,
        result
      }));
    } catch (err: any) {
      console.error('❌ Erro no teste de conexão:', err);

      let errorMessage = 'Erro ao testar conexão';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Timeout: O teste de conexão demorou muito tempo. Verifique a conectividade da OLT.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setTestConnectionDialog(prev => ({
        ...prev,
        testing: false,
        result: {
          success: false,
          message: errorMessage,
          error: err.message,
          ssh: { success: false, message: 'Falha no teste SSH' },
          snmp: { success: false, message: 'Falha no teste SNMP' }
        }
      }));
    }
  };

  const handleCloseTestDialog = () => {
    setTestConnectionDialog({
      open: false,
      olt: null,
      testing: false,
      result: null
    });
  };

  const handleToggleStatus = async (olt: OLT) => {
    try {
      await oltService.toggleStatus(olt.id);
      loadOLTs(); // Recarregar dados
    } catch (err: any) {
      console.error('Erro ao alterar status:', err);
      setError(err.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteOLT = async (olt: OLT) => {
    // Verificar se a OLT tem portas PON associadas
    if (olt.pon_ports && olt.pon_ports.length > 0) {
      toast.error('Não é possível excluir OLT que possui portas PON cadastradas');
      return;
    }

    // Confirmação antes de deletar
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir a OLT "${olt.name}"?\n\nEsta ação irá:\n- Desativar a OLT (ativo = false)\n- Excluir permanentemente a OLT\n\nEsta ação não pode ser desfeita.`
    );

    if (!confirmed) {
      return;
    }

    try {
      // Primeiro, desativar a OLT (mudar ativo para false)
      if (olt.ativo) {
        await oltService.toggleStatus(olt.id);
        console.log('🔒 OLT desativada antes da exclusão');
      }

      // Depois, excluir a OLT
      await oltService.delete(olt.id);
      toast.success('OLT desativada e excluída com sucesso!');
      loadOLTs(); // Recarregar lista
    } catch (err: any) {
      console.error('Erro ao excluir OLT:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao excluir OLT';
      toast.error(errorMessage);
    }
  };

  const handleOpenOLTForm = (olt?: OLT) => {
    setOltFormDialog({
      open: true,
      olt: olt || null
    });
  };

  useEffect(() => {
    if (createTrigger === 0) return;
    handleOpenOLTForm();
  }, [createTrigger]);

  const handleCloseOLTForm = () => {
    setOltFormDialog({
      open: false,
      olt: null
    });
  };

  const handleOLTFormSuccess = (olt: OLT) => {
    console.log('🎉 handleOLTFormSuccess chamada com OLT:', olt);
    console.log('🔒 Fechando modal...');
    handleCloseOLTForm();
    console.log('🔄 Recarregando lista de OLTs...');
    loadOLTs(); // Recarregar lista
    console.log('✅ handleOLTFormSuccess concluída');
  };

  const handleViewOLT = (olt: OLT) => {
    navigate(`/ftth/olts/${olt.id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'offline':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'maintenance':
        return <PauseIcon color="warning" fontSize="small" />;
      default:
        return <SettingsIcon fontSize="small" />;
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 1 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nome, IP ou localização"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Fabricante</InputLabel>
              <Select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                label="Fabricante"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="datacom">Datacom</MenuItem>
                <MenuItem value="huawei">Huawei</MenuItem>
                <MenuItem value="vsolution">VSolution</MenuItem>
                <MenuItem value="fdata">FData</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="maintenance">Manutenção</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadOLTs}
                disabled={loading}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              >
                Atualizar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                },
              }}>
                <TableCell>Nome</TableCell>
                <TableCell>Fabricante/Modelo</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Localização</TableCell>
                <TableCell>Portas</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : olts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhuma OLT encontrada
                  </TableCell>
                </TableRow>
              ) : (
                olts.map((olt) => (
                  <TableRow key={olt.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box onClick={() => handleViewOLT(olt)} style={{ cursor: 'pointer' }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                          {olt.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Criado em {new Date(olt.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                          {olt.vendor.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {olt.model}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                        {olt.ip_address}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        SSH: {olt.ssh_port}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        icon={getStatusIcon(olt.status)}
                        label={olt.status}
                        color={getStatusColor(olt.status) as any}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {olt.location || 'Não informado'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {olt.pon_ports?.length || 0} portas
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Box display="flex" gap={0.25}>
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewOLT(olt)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenOLTForm(olt)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Testar Conexão">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleTestConnection(olt)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={olt.ativo ? 'Desativar' : 'Ativar'}>
                          <IconButton
                            size="small"
                            color={olt.ativo ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(olt)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            {olt.ativo ? <PauseIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteOLT(olt)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.5,
              px: 2,
              bgcolor: alpha(primario, 0.04),
              borderTop: `2px solid ${alpha(primario, 0.2)}`,
            }}
          >
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  minHeight: 34,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-actions': { ml: 0.5 },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Modal para Formulário OLT */}
      <Dialog
        open={oltFormDialog.open}
        onClose={handleCloseOLTForm}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <OLTForm
            olt={oltFormDialog.olt || undefined}
            onSuccess={handleOLTFormSuccess}
            onCancel={handleCloseOLTForm}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de Teste de Conexão */}
      <Dialog
        open={testConnectionDialog.open}
        onClose={handleCloseTestDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Teste de Conexão - {testConnectionDialog.olt?.name}
        </DialogTitle>
        <DialogContent>
          {testConnectionDialog.testing ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={3}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Testando conexão...</Typography>
            </Box>
          ) : testConnectionDialog.result ? (
            <Box>

              {/* Resultados Individuais SSH e SNMP */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Teste SSH
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {testConnectionDialog.result.ssh?.success ? (
                          <CheckCircleIcon color="success" fontSize="large" />
                        ) : (
                          <ErrorIcon color="error" fontSize="large" />
                        )}
                        <Typography variant="h6" color={testConnectionDialog.result.ssh?.success ? 'success.main' : 'error.main'}>
                          {testConnectionDialog.result.ssh?.success ? 'OK' : 'Falha'}
                        </Typography>
                      </Box>
                      {testConnectionDialog.result.ssh?.message && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {testConnectionDialog.result.ssh.message}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Teste SNMP
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {testConnectionDialog.result.snmp?.success ? (
                          <CheckCircleIcon color="success" fontSize="large" />
                        ) : (
                          <ErrorIcon color="error" fontSize="large" />
                        )}
                        <Typography variant="h6" color={testConnectionDialog.result.snmp?.success ? 'success.main' : 'error.main'}>
                          {testConnectionDialog.result.snmp?.success ? 'OK' : 'Falha'}
                        </Typography>
                      </Box>
                      {testConnectionDialog.result.snmp?.message && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {testConnectionDialog.result.snmp.message}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Informações do Equipamento (apenas se pelo menos um teste passou) */}
              {testConnectionDialog.result.success && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Informações do Equipamento
                    </Typography>

                    <Grid container spacing={2}>
                      {testConnectionDialog.result.vendor && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Fabricante
                          </Typography>
                          <Typography variant="body1">
                            {testConnectionDialog.result.vendor}
                          </Typography>
                        </Grid>
                      )}

                      {testConnectionDialog.result.model && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Modelo
                          </Typography>
                          <Typography variant="body1">
                            {testConnectionDialog.result.model}
                          </Typography>
                        </Grid>
                      )}

                      {testConnectionDialog.result.version && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Versão
                          </Typography>
                          <Typography variant="body1">
                            {testConnectionDialog.result.version}
                          </Typography>
                        </Grid>
                      )}

                      {testConnectionDialog.result.uptime && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Uptime
                          </Typography>
                          <Typography variant="body1">
                            {testConnectionDialog.result.uptime}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OltsTab; 